import {queries} from '@/shared/integrations/zero'
import {Group} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {ListUserGroupsInput} from '@cypro/engine/queries'
import {useCallback, useMemo, useState} from 'react'

export type UserGroupStartRow = Pick<Group, 'id' | 'createdAt' | 'name'>

export function useGetInfiniteUserGroups({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit = 25,
}: Omit<Partial<ListUserGroupsInput>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.dir, sort?.field, filters?.search, filters?.status, limit, search]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getPageQuery: GetPageQuery<Group, UserGroupStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {id: start.id, direction: dir, createdAt: start.createdAt, name: start.name}
          : undefined

      return {
        query: queries.userGroups.listUserGroups({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters, ...(search ? {search} : {})},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<Group> = useCallback(
    ({id}) => ({
      query: queries.userGroups.getGroupById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: Group): UserGroupStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback((row: Group) => ({label: row.name, value: row.id}), [])

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
